import React, { useCallback, useEffect, useState } from 'react'
import { ScrollView, Pressable, useWindowDimensions } from 'react-native';
import Modal from 'react-native-modal';
import { Text, View, Title, BlurView } from '@constants/Themed';
import { EvilIcons, AntDesign } from '@expo/vector-icons';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import parseByDelimiter from '@utils/parseByDelimiter';
import TagInput from './TagInput';
import Button from '../UI/Button/Button';
import AnimatedLottieView from '@utils/lottieAnimation';
import AnimatedTextFunction from '@hooks/animateTextEntry';
import apis from '@api/api';
import { SizableText } from 'tamagui';
import { uniqueId } from 'lodash';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

const toCapitalCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const TagComponent = props => {

  //Calculate minWidth based on text length
  const minWidth = props.text.length * 10;

  return (
    <View style={[styles.centerEverything, {...props.tagStyle, gap: 10, minWidth, maxWidth: minWidth + 40, justifyContent: 'center', flexDirection: "row", alignItems: "center", paddingHorizontal: 8, backgroundColor: 'rgb(90,192,234)', borderRadius: 20, maxHeight: 40 }]}>
      <SizableText allowFontScaling={false} numberOfLines={1} style={[styles.centerEverything, { textAlign: 'center', verticalAlign: "middle", justifyContent: "center", alignItems:"center", fontSize: 15, fontWeight: '600', color: "white", paddingHorizontal: 8 }]} >{parseByDelimiter(toCapitalCase(props.text))}</SizableText>
    </View>
  )
}


const TagViewer = ({ tagStyle, tags, item = {
  _id: "",
  dataTitle: "",
  tags: []
} }: {
  tagStyle?: any,
  tags: Array<string>,
  item: {
    _id: string,
    dataTitle: string,
    tags: Array<string>
  },
}) => {
  const [visible, setVisible] = useState(false);
  const { width, height } = useWindowDimensions();
  const [aiTags, setAiTags] = useState([]);
  const theme = themeSelector(); 

  const handleShowAddTag = useCallback(() => {
    setVisible(true);
  }, [tags]);


  const AddTagModal = () => {
    const [newTags, setNewTags] = useState([]);
    const [next, setNext] = useState(false);

    useEffect(() => {
      setNewTags(tags);
    }, [tags, item]);

    const handleAddTag = async (tags) => {
      console.log(tags);
      await apis.addTagsToData(item._id, tags).then(res => {
        setVisible(false);
      })
    }

    const compareTags = () => {
      if (tags === newTags || tags?.length === newTags.length) {
        return true;
      } else return false;
    }
    

    return (
      <Modal isVisible={visible}
        onBackdropPress={() => setVisible(false)}
        animationOut={'slideOutDown'}
        deviceHeight={height}
        deviceWidth={width}
        style={{ flex: 1 }}
      >
        <View style={{ width: '100%', maxWidth: 600, backgroundColor: theme ? "white" : "black", borderRadius: 20, alignSelf: 'center', padding: 15, gap: 30, maxHeight: '85%', paddingHorizontal: 30 }}>
          <AnimatedLottieView autoPlay loop={false} style={{ width: '100%', maxWidth: 200, height: 150, alignSelf: 'center' }} source={require('@assets/animations/65034-label.json')} />
          <Title style={{ textAlign: "center", fontWeight: "bold" }}>Add tags to {item?.dataTitle ? parseByDelimiter(item?.dataTitle || "Unnamed dataset") : "Unnamed dataset"}</Title>
          <View style={{ width: '100%' }}>
            <TagInput placeHolder="Add tags" tags={newTags} setTags={setNewTags} enabled />
          </View>
          <View style={{ width: '100%', paddingHorizontal: 20 }}>
            <View style={{ width: '100%', alignItems: 'center' }}>
              <AnimatedTextFunction fontColor={theme ? 'black' : "white"} onAnimationComplete={() => setNext(true)} value={'Adding tags can help SmartSaaS understand your data better, giving you better suggestions and further enabling automated actions, so be sure to utilise this feature as much as possible.'} fontSize={0} />
            </View>
            {
              next &&
              <View style={{ width: '100%', alignItems: 'center' }}>
                <AnimatedTextFunction fontColor={theme ? 'black' : "white"} value={'You can also tag departments within your organisation to help you easily delegate access control.'} fontSize={0} />
              </View>
            }
          </View>
          <View style={[styles.defaultRoundedShadow, { width: '100%', minHeight: 120 }]}>
            <Title style={{ paddingHorizontal: 20, textAlign: "center" }}>AI suggestions</Title>
            <View style={{ flexDirection: 'row', width: '100%' }}>
              <AnimatedLottieView autoPlay loop style={{ width: 80, height: 80, alignSelf: 'center' }} source={require('@assets/animations/automation_robot.json')} />
              <View style={{ flex: 1, paddingRight: 10 }}>
                <TagViewer tagStyle={{}} tags={aiTags || []} item={undefined} />
              </View>
            </View>
          </View>
          <View style={{ width: '100%', flexDirection: 'row', justifyContent: 'space-between', gap: 10, paddingHorizontal: '5%' }}>
            <Button customStyle={{ flex: 1, maxWidth: 150, textAlign: 'center' }} buttonText="Close" defaultColor='red' onPress={() => setVisible(false)} />
            <Button customStyle={{ flex: 1, maxWidth: 150, textAlign: 'center' }} buttonText="Add Tags" onPress={() => handleAddTag(newTags)} disabled={compareTags()} />
          </View>
        </View>
      </Modal>
    )
  }


  return (
    <View style={{ width: '100%', zIndex: 2, minHeight: 40, alignSelf: "stretch", backgroundColor: 'transparent' }}>
      <View style={{ width: '100%', flexDirection: 'row', gap: 10, minHeight: 40, maxHeight: 55, backgroundColor: 'transparent' }}>
        <AddTagModal />
        <Animatable.View useNativeDriver animation={'fadeInLeft'} style={{ width: 35, minWidth: 35, maxWidth: 35 }}>
          <Pressable style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }} onPress={() => handleShowAddTag()}>
            <AntDesign name="tags" size={36} color="rgb(90,192,234)" />
          </Pressable>
        </Animatable.View>
        {
          tags && tags.length > 0 &&
          <ScrollView 
            horizontal 
            contentContainerStyle={{ gap: 10, marginBottom: 10, paddingHorizontal: 5 }} 
            style={{ flex: 1, maxHeight: 40, minHeight: 40 }}
            showsHorizontalScrollIndicator={false}
          >
              {tags.map((tag, index) => <TagComponent tagStyle={tagStyle} text={tag} key={uniqueId(tag + index)} />)}
        </ScrollView>
        }
      </View>
    </View>
  )
}

export default TagViewer;